import { useEffect, useRef, useState } from 'react';
import { HiX, HiPlus, HiMinus, HiRefresh } from 'react-icons/hi';
import useScrollLock from '../utils/useScrollLock';
import '../styles/ImageLightbox.css';

/*
  Amazon-style product image viewer:
  - click outside / ✕ / Esc to close
  - + / − buttons, mouse wheel, double-click to zoom
  - drag to pan when zoomed in
  - pinch-to-zoom + one-finger pan on touch devices
*/
const MIN_SCALE = 1;
const MAX_SCALE = 5;
const STEP = 0.5;

const clamp = (v) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, v));

export default function ImageLightbox({ src, alt, onClose }) {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null); // { startX, startY, origX, origY }
  const pinchRef = useRef(null); // { startDist, startScale }

  const zoomTo = (next) => {
    const s = clamp(next);
    setScale(s);
    if (s === MIN_SCALE) setPos({ x: 0, y: 0 });
  };

  useScrollLock(true);

  /* Esc to close, and the zoom shortcuts */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === '+' || e.key === '=') zoomTo(scale + STEP);
      if (e.key === '-') zoomTo(scale - STEP);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  /* ---- mouse ---- */
  const onWheel = (e) => {
    zoomTo(scale + (e.deltaY < 0 ? STEP : -STEP));
  };

  const onMouseDown = (e) => {
    if (scale === MIN_SCALE) return;
    e.preventDefault();
    setDragging(true);
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y };
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const d = dragRef.current;
      setPos({ x: d.origX + (e.clientX - d.startX), y: d.origY + (e.clientY - d.startY) });
    };
    const onUp = () => setDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging]);

  const onDoubleClick = () => {
    zoomTo(scale > MIN_SCALE ? MIN_SCALE : 2);
  };

  /* ---- touch (pan + pinch) ---- */
  const touchDist = (t) =>
    Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      pinchRef.current = { startDist: touchDist(e.touches), startScale: scale };
      dragRef.current = null;
    } else if (e.touches.length === 1 && scale > MIN_SCALE) {
      dragRef.current = {
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        origX: pos.x,
        origY: pos.y,
      };
    }
  };

  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchRef.current) {
      const ratio = touchDist(e.touches) / pinchRef.current.startDist;
      zoomTo(pinchRef.current.startScale * ratio);
    } else if (e.touches.length === 1 && dragRef.current) {
      const d = dragRef.current;
      setPos({
        x: d.origX + (e.touches[0].clientX - d.startX),
        y: d.origY + (e.touches[0].clientY - d.startY),
      });
    }
  };

  const onTouchEnd = () => {
    pinchRef.current = null;
    dragRef.current = null;
  };

  if (!src) return null;

  return (
    <div className="lightbox-overlay" onClick={onClose}>
      {/* top bar */}
      <div className="lightbox-topbar" onClick={(e) => e.stopPropagation()}>
        <span className="lightbox-title">{alt}</span>
        <button className="lightbox-btn" onClick={onClose} aria-label="Close">
          <HiX />
        </button>
      </div>

      {/* image stage */}
      <div
        className="lightbox-stage"
        onClick={(e) => e.stopPropagation()}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onDoubleClick={onDoubleClick}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{
          cursor: scale > MIN_SCALE ? (dragging ? 'grabbing' : 'grab') : 'zoom-in',
        }}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          style={{
            transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
            transition: dragging ? 'none' : 'transform 0.25s ease',
          }}
        />
      </div>

      {/* zoom controls */}
      <div className="lightbox-controls" onClick={(e) => e.stopPropagation()}>
        <button
          className="lightbox-btn"
          onClick={() => zoomTo(scale - STEP)}
          disabled={scale <= MIN_SCALE}
          aria-label="Zoom out"
        >
          <HiMinus />
        </button>
        <span className="lightbox-zoom-level">{Math.round(scale * 100)}%</span>
        <button
          className="lightbox-btn"
          onClick={() => zoomTo(scale + STEP)}
          disabled={scale >= MAX_SCALE}
          aria-label="Zoom in"
        >
          <HiPlus />
        </button>
        <button
          className="lightbox-btn"
          onClick={() => zoomTo(MIN_SCALE)}
          disabled={scale === MIN_SCALE}
          aria-label="Reset zoom"
        >
          <HiRefresh />
        </button>
      </div>

      <p className="lightbox-hint">
        Scroll or pinch to zoom · drag to move · double-click to toggle
      </p>
    </div>
  );
}
